/**
 * The portal: sign in, order from what is in stock, see my orders.
 *
 * Online by nature (ADR 0011): it is used on customers' own devices, and an
 * offline copy would put the nursery's data on them. So it says plainly when
 * the device is offline rather than pretending.
 *
 * Signed in, the first call is `portal_me`. A login the nursery turned off
 * answers `PT403`, and the page says so and signs out rather than showing an
 * empty list that looks like a quiet week.
 */

import { type Session, createClient } from '@supabase/supabase-js';
import { useEffect, useMemo, useState } from 'react';

import { OrdersView } from './OrdersView.js';
import { OrderView } from './OrderView.js';
import type { Settled } from './placing.js';
import { type Me, portalApi } from './portal-api.js';
import { SETTINGS, configured } from './settings.js';
import { SignIn } from './SignIn.js';

type View = 'order' | 'orders';

function useOnline(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const up = (): void => {
      setOnline(true);
    };
    const down = (): void => {
      setOnline(false);
    };
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);
  return online;
}

export function App(): React.JSX.Element {
  if (!configured()) {
    return (
      <main className="shell shell--narrow">
        <h1>Trade orders</h1>
        <p className="notice notice--bad" role="alert">
          This copy of the portal was built without a project to talk to.
        </p>
      </main>
    );
  }
  return <Connected />;
}

function Connected(): React.JSX.Element {
  const client = useMemo(() => createClient(SETTINGS.url, SETTINGS.publishableKey), []);
  const [refused, setRefused] = useState(false);
  const api = useMemo(
    () =>
      portalApi(client, {
        onNoAccess: () => {
          setRefused(true);
          void client.auth.signOut();
        },
      }),
    [client],
  );
  const online = useOnline();
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const [me, setMe] = useState<Me | undefined>(undefined);
  const [unreachable, setUnreachable] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [view, setView] = useState<View>('order');
  const [placed, setPlaced] = useState<Settled | undefined>(undefined);

  useEffect(() => {
    void client.auth.getSession().then(({ data }) => {
      setSession(data.session);
    });
    const { data } = client.auth.onAuthStateChange((_event, next) => {
      setSession(next);
    });
    return () => {
      data.subscription.unsubscribe();
    };
  }, [client]);

  useEffect(() => {
    if (session === null || session === undefined) {
      setMe(undefined);
      return;
    }
    void api.me().then((answer) => {
      if (answer.ok) {
        setMe(answer.value);
        setRefused(false);
        setUnreachable(false);
        return;
      }
      // No access has already signed out, through the api's hook.
      if (answer.error.kind !== 'no-access') setUnreachable(true);
    });
  }, [api, session, attempt]);

  const offline = online ? null : (
    <p className="notice notice--warn" role="status">
      This device is offline. Ordering needs a connection.
    </p>
  );

  if (session === undefined) {
    return <main className="shell" aria-busy="true" />;
  }

  if (session === null) {
    return (
      <>
        {offline}
        {refused ? (
          <p className="notice notice--bad shell shell--narrow" role="alert">
            That login no longer has access to ordering. Ask the nursery.
          </p>
        ) : null}
        <SignIn client={client} />
      </>
    );
  }

  return (
    <div className="shell">
      <header className="top">
        <div>
          <span className="eyebrow">San Michell Farms</span>
          <h1>{me?.account.tradingName ?? 'Trade orders'}</h1>
        </div>
        <button
          className="button button--quiet"
          type="button"
          onClick={() => {
            void client.auth.signOut();
          }}
        >
          Sign out
        </button>
      </header>
      <nav className="tabs" aria-label="Portal">
        <button
          className="tabs__tab"
          type="button"
          aria-current={view === 'order' ? 'page' : undefined}
          onClick={() => {
            setView('order');
          }}
        >
          Order
        </button>
        <button
          className="tabs__tab"
          type="button"
          aria-current={view === 'orders' ? 'page' : undefined}
          onClick={() => {
            setView('orders');
          }}
        >
          My orders
        </button>
      </nav>
      {offline}
      <main>
        {me === undefined && unreachable ? (
          <div className="stack">
            <p className="notice notice--bad" role="alert">
              We could not reach the nursery.
            </p>
            <button
              className="button"
              type="button"
              onClick={() => {
                setUnreachable(false);
                setAttempt((was) => was + 1);
              }}
            >
              Try again
            </button>
          </div>
        ) : me === undefined ? (
          <p className="faint">Signing you in…</p>
        ) : view === 'order' ? (
          <OrderView
            api={api}
            me={me}
            onPlaced={(settled) => {
              setPlaced(settled);
              setView('orders');
            }}
          />
        ) : (
          <OrdersView api={api} placed={placed} />
        )}
      </main>
    </div>
  );
}
